import React, { useEffect, useRef, useCallback } from 'react'
import { styled } from 'styled-components'
import { type MediaRef, VOLUMES } from '../../../utility'
import {
	useSelectedAudioOutput,
	useMissionPauseForRef,
} from '../../../sound/hooks'
import type { Overlay } from '@mission.io/mission-toolkit'
import {
	useOnVideoOverlayEnd,
	useShowErrors,
	useResolvers,
	useIsSilent,
	useVolumeOf,
} from '../../../context'

type Props = {
	overlay: Overlay,
	teacherView?: boolean,
	handleError?: (message: string) => void,
}

export default function VideoOverlay({
	overlay,
	handleError,
}: Props): React$Node {
	const { videoSourceResolver } = useResolvers()
	const onVideoOverlayEnd = useOnVideoOverlayEnd()
	const currentVideoOverlayUrl = overlay.url
	const videoVolume = useVolumeOf(VOLUMES.VIDEO)
	const video: { current: HTMLVideoElement | null } = useRef(null)

	useSelectedAudioOutput(((video: any): MediaRef))
	useMissionPauseForRef(((video: any): MediaRef))
	const showErrors = useShowErrors()
	const isSilent = useIsSilent()

	const overlayFinished = () => {
		onVideoOverlayEnd(overlay._actionId)
	}

	const handleVideoError = () => {
		handleError && handleError('Video content could not be found.')
		overlayFinished()
	}
	const extraProps = {}
	extraProps.onEnded = overlayFinished
	if (showErrors) {
		extraProps.onError = handleVideoError
	}

	const updateVolumes = useCallback(() => {
		if (video.current) {
			video.current.volume = videoVolume
		}
	}, [video, videoVolume])

	useEffect(() => {
		updateVolumes()
	}, [videoVolume, updateVolumes])

	return (
		<OverlayVideo
			ref={video}
			autoPlay
			onPlay={() => {
				updateVolumes()
			}}
			muted={isSilent}
			src={videoSourceResolver(currentVideoOverlayUrl)}
			{...extraProps}
		/>
	)
}

const OverlayVideo = styled.video`
	max-width: 100%;
	max-height: 100%;
	display: block;
	${({ theme }) =>
		`border: ${theme.spacing} ${theme.onBase} solid;
	background-color: ${theme.base4};
	border-radius: 4px;`}
`
